import React, {FC, useEffect, useMemo} from 'react';
import pick from 'lodash.pick';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {getLocale} from '@epic-core/common/dist';
import {Datapoint, dateFormatter} from '@epic-visualizations/charts';
import {useComparisonColor, useVisualizationsTheme} from '@epic-visualizations/theme';

import useWidth from './useWidth';
import Legends from './legends/Legends';
import {getCompactXAxis} from './compact-axis/CustomXAxis';
import {getCompactYAxis} from './compact-axis/CustomYAxis';
import {ChartSkeleton} from '../../../../../common/skeleton';
import RewardsTooltip from './RewardsTooltip';
import {getColor} from './utils';
import useInteractiveLegends from './legends/useInteractiveLegends';
import {KeysMap, Trends} from './types';
import {Option} from '../../../../../../types';
import {REWARDS_DATA_COLORS, COMPACT_CURRENCY_FORMAT, CURRENCY_FORMAT} from '../../constants';

import {StyledBarChart, ChartContainer} from './styles';

export interface ChartProps {
    data: Datapoint[];
    loading: boolean;
    selectedGames: Option[];
    trends?: Trends;
    height?: number;
    limit?: number;
    keysMap?: KeysMap;
}

const RewardsBarChart: FC<ChartProps> = ({
    data: propsData,
    trends,
    limit = 10,
    loading,
    selectedGames,
    keysMap = {},
    ...props
}: ChartProps) => {
    const theme = useTheme();
    const getComparisonColor = useComparisonColor();
    const visualizationsTheme = useVisualizationsTheme();

    const isFmdBreakpoint = !useMediaQuery(theme.breakpoints.up('fmd'));
    const [containerRef, width] = useWidth();

    const locale = getLocale();

    const xTickFormatter = dateFormatter(locale, {month: 'short'});
    const yTickFormatter = value =>
        new Intl.NumberFormat(locale, COMPACT_CURRENCY_FORMAT).format(value);
    const tooltipLabelFormatter = dateFormatter(locale, {month: 'short', year: 'numeric'});
    const tooltipFormatter = value =>
        new Intl.NumberFormat(
            locale,
            value < 1000 ? CURRENCY_FORMAT : COMPACT_CURRENCY_FORMAT
        ).format(value);

    // Used to display legends list
    const dataKeys = useMemo(() => selectedGames.map(({value}) => String(value)), [selectedGames]);

    const {
        hoveredKey,
        hiddenKeys,
        setHiddenKeys,
        onLegendClick,
        onLegendMouseEnter,
        onLegendMouseLeave
    } = useInteractiveLegends();

    useEffect(() => {
        setHiddenKeys(Array.from(new Set(dataKeys.slice(limit, dataKeys.length))));
    }, [dataKeys]);

    // Override the default BarChart colors for hover effect
    const dataColors = useMemo(
        () =>
            dataKeys.reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: getColor(
                        getComparisonColor(REWARDS_DATA_COLORS[key]),
                        hoveredKey && !hiddenKeys.includes(hoveredKey) && hoveredKey !== key
                    )
                }),
                {}
            ),
        [getComparisonColor, hoveredKey, dataKeys]
    );

    const data = useMemo(() => {
        const keysToShow = ['time', ...dataKeys.filter(key => !hiddenKeys.includes(key))];

        return propsData.map(item => pick(item, keysToShow));
    }, [propsData, hiddenKeys, dataKeys]);

    if (loading) {
        return <ChartSkeleton pb={2} pt={4} height={520} width="100%" turnOver />;
    }

    return (
        <>
            <ChartContainer ref={containerRef}>
                <StyledBarChart
                    responsive
                    stacked
                    showLegend={false}
                    locales={locale}
                    data={data}
                    dataColors={dataColors}
                    margin={{top: 5, right: 0, bottom: 0, left: 0}}
                    yTickFormatter={yTickFormatter}
                    xTickFormatter={xTickFormatter}
                    tooltipLabelFormatter={tooltipLabelFormatter}
                    tooltipContent={RewardsTooltip({
                        trends,
                        tooltipFormatter
                    })}
                    xAxisProps={
                        isFmdBreakpoint
                            ? getCompactXAxis({
                                  visualizationsTheme,
                                  theme,
                                  width,
                                  dataLength: data.length,
                                  xTickFormatter
                              })
                            : {}
                    }
                    yAxisProps={
                        isFmdBreakpoint
                            ? getCompactYAxis({
                                  visualizationsTheme,
                                  yTickFormatter
                              })
                            : {}
                    }
                    {...props}></StyledBarChart>
            </ChartContainer>
            <Legends
                keysMap={keysMap}
                limit={limit}
                hoveredKey={hoveredKey}
                hiddenKeys={hiddenKeys}
                dataKeys={dataKeys}
                dataColors={REWARDS_DATA_COLORS}
                selectedGames={selectedGames}
                onClick={onLegendClick}
                onMouseEnter={onLegendMouseEnter}
                onMouseLeave={onLegendMouseLeave}
            />
        </>
    );
};

export default RewardsBarChart;
